import React, { useState, useEffect } from 'react';
import { ChevronDown, Calendar, User, Check } from 'lucide-react';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useThemeStore } from '../stores/themeStore';

interface CalendarUser {
  id: string;
  name: string;
  email: string;
  photoURL?: string;
}

interface CalendarSwitcherProps {
  selectedUserId: string;
  onSelectUser: (userId: string) => void;
}

export default function CalendarSwitcher({ selectedUserId, onSelectUser }: CalendarSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<CalendarUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { darkMode } = useThemeStore();

  useEffect(() => {
    loadAccessibleCalendars();
  }, []);

  const loadAccessibleCalendars = async () => {
    if (!auth.currentUser) return;

    setIsLoading(true);
    try {
      // Récupérer les informations de l'utilisateur actuel
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setIsLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const accessibleCalendars = userData.hasAccessToCalendars || [];
      
      // Ajouter l'utilisateur actuel à la liste
      const currentUser: CalendarUser = {
        id: auth.currentUser.uid,
        name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Moi',
        email: auth.currentUser.email || '',
        photoURL: userData.photoURL
      };
      
      const usersList: CalendarUser[] = [currentUser];
      
      // Récupérer les informations des utilisateurs dont on a accès au calendrier
      for (const userId of accessibleCalendars) {
        const sharedUserRef = doc(db, 'users', userId);
        const sharedUserDoc = await getDoc(sharedUserRef);
        
        if (sharedUserDoc.exists()) {
          const sharedUserData = sharedUserDoc.data();
          usersList.push({
            id: userId,
            name: `${sharedUserData.firstName || ''} ${sharedUserData.lastName || ''}`.trim() || sharedUserData.email.split('@')[0],
            email: sharedUserData.email,
            photoURL: sharedUserData.photoURL
          });
        }
      }
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading accessible calendars:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const selectedUser = users.find(user => user.id === selectedUserId) || users[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          darkMode 
            ? 'bg-gray-700 hover:bg-gray-600 text-white' 
            : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200'
        }`}
      >
        <span className="text-sm font-medium">
          {selectedUser?.name || 'Calendrier'}
        </span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div 
          className={`absolute z-10 mt-1 w-56 rounded-md shadow-lg ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          <div className="py-1">
            {users.map(user => (
              <button
                key={user.id}
                className={`w-full text-left px-4 py-2 flex items-center gap-2 ${
                  darkMode 
                    ? 'hover:bg-gray-700 text-white' 
                    : 'hover:bg-gray-50 text-gray-800'
                }`}
                onClick={() => {
                  onSelectUser(user.id);
                  setIsOpen(false);
                }}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {user.email}
                  </div>
                </div>
                {user.id === selectedUserId && (
                  <Check className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}